import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, MessageSquare, Users, LogIn } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import axios from "axios";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { staffLogin, ADMIN_TOKEN_KEY, apiErr } from "@/lib/authApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });
const COLORS = ["#E23E47", "#E0A526", "#3B82F6", "#3FBF7F", "#C084FC", "#FF4C4C", "#8B8378", "#F97316", "#14B8A6", "#EC4899", "#A3E635", "#94A3B8", "#5A544D"];

export default function ChatInsights() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Insights />;
}

function Insights() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);

  const load = async () => {
    setBusy(true);
    try { setData((await axios.get(`${API}/admin/chat/insights`, { params: { days }, headers: hdr() })).data); }
    catch (e) { toast.error(apiErr(e)); if (e?.response?.status === 401) localStorage.removeItem(ADMIN_TOKEN_KEY); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const generate = async () => {
    setGen(true);
    try {
      const r = await axios.post(`${API}/admin/chat/insights/generate`, null, { params: { days }, headers: hdr() });
      setData((d) => ({ ...d, latest_report: r.data }));
      toast.success("AI report generated");
    } catch (e) { toast.error(apiErr(e)); } finally { setGen(false); }
  };

  const rep = data?.latest_report;
  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Admin · Chat Insights</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">What visitors keep asking.</h1>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button key={d} data-testid={`insights-days-${d}`} onClick={() => setDays(d)} className={d === days ? "btn-red" : "btn-ghost"} style={{ padding: "8px 14px" }}>{d}d</button>
            ))}
            <button data-testid="insights-refresh" className="btn-ghost" onClick={load} disabled={busy}>{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
          </div>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <Stat icon={MessageSquare} label="Questions" value={data.total_questions} testid="insights-total" />
              <Stat icon={Users} label="Chat sessions" value={data.sessions} testid="insights-sessions" />
              <Stat icon={MessageSquare} label="Avg / session" value={data.avg_per_session} testid="insights-avg" />
              <Stat icon={Sparkles} label="Top topic" value={data.topics[0]?.topic || "—"} small testid="insights-top-topic" />
            </div>

            <div className="grid lg:grid-cols-5 gap-6 mt-6">
              <Panel className="p-6 lg:col-span-2">
                <MonoLabel>Topic mix</MonoLabel>
                {data.topics.length ? (
                  <div className="mt-4" style={{ height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={data.topics} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="topic" width={130} tick={{ fill: "#C2BCB3", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#14171C", border: "1px solid #2A2F38", fontSize: 12 }} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>{data.topics.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <Empty />}
              </Panel>

              <Panel className="p-6 lg:col-span-3">
                <MonoLabel>Most frequent questions</MonoLabel>
                <div className="mt-4 divide-y" style={{ borderColor: "var(--hairline)" }} data-testid="insights-top-questions">
                  {data.top_questions.length ? data.top_questions.map((q, i) => (
                    <div key={i} className="flex items-start gap-3 py-3" data-testid="insights-question-row">
                      <span className="mono-label" style={{ minWidth: 26, color: COLORS[data.topics.findIndex((t) => t.topic === q.topic) % COLORS.length] }}>{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm" style={{ color: "var(--text)" }}>{q.question}</div>
                        <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{q.topic}</div>
                      </div>
                      <span className="chip" data-testid="insights-question-count">×{q.count}</span>
                    </div>
                  )) : <Empty />}
                </div>
              </Panel>
            </div>

            <Panel className="p-6 mt-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <MonoLabel>AI report · FAQ & copy suggestions</MonoLabel>
                  {rep && <div className="text-xs mt-1" style={{ color: "var(--taupe)" }}>Generated {new Date(rep.created_at).toLocaleString("en-IN")} · {rep.question_count} questions analysed</div>}
                </div>
                <button data-testid="insights-generate" className="btn-red" onClick={generate} disabled={gen || data.total_questions < 3}>
                  {gen ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {gen ? "Analysing…" : rep ? "Regenerate" : "Generate report"}
                </button>
              </div>
              {data.total_questions < 3 && <div className="text-sm mt-3" style={{ color: "var(--taupe)" }}>Need at least 3 visitor questions before AI analysis.</div>}
              {rep && (
                <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="insights-report">
                  <Section title="Suggested FAQs" color="var(--green)">
                    {(rep.faq_suggestions || []).map((f, i) => (
                      <div key={i} className="cb-callout cb-tip" style={{ display: "block" }}>
                        <div className="font-semibold text-sm">{f.question}</div>
                        <div className="text-sm mt-1" style={{ opacity: .85 }}>{f.suggested_answer}</div>
                      </div>
                    ))}
                  </Section>
                  <Section title="Copy improvements" color="var(--amber)">
                    {(rep.copy_improvements || []).map((c, i) => (
                      <div key={i} className="cb-callout cb-price" style={{ display: "block" }}>
                        <div className="mono-label" style={{ color: "var(--amber)" }}>{c.section}</div>
                        <div className="text-sm mt-1"><strong>Issue:</strong> {c.issue}</div>
                        <div className="text-sm mt-1"><strong>Fix:</strong> {c.suggestion}</div>
                      </div>
                    ))}
                  </Section>
                  <Section title="Themes" color="var(--blue)">
                    {(rep.themes || []).map((t, i) => (
                      <div key={i} className="cb-callout cb-info" style={{ display: "block" }}>
                        <div className="font-semibold text-sm">{t.theme} <span className="chip ml-2">~{t.count_estimate}</span></div>
                        <div className="text-sm mt-1" style={{ opacity: .85 }}>{t.summary}</div>
                      </div>
                    ))}
                  </Section>
                  <Section title="Unanswered gaps" color="var(--red-3)">
                    {(rep.unanswered_gaps || []).map((g, i) => <div key={i} className="cb-callout cb-warn">{g}</div>)}
                  </Section>
                </div>
              )}
            </Panel>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Stat({ icon: Icon, label, value, small, testid }) {
  return (
    <Panel className="p-5">
      <div className="flex items-center gap-2"><Icon size={13} style={{ color: "var(--red-3)" }} /><MonoLabel>{label}</MonoLabel></div>
      <div data-testid={testid} className={`font-serif mt-2 ${small ? "text-lg" : "text-3xl"}`} style={{ color: "var(--text)" }}>{value}</div>
    </Panel>
  );
}

function Section({ title, color, children }) {
  return (
    <div>
      <div className="mono-label mb-3" style={{ color, borderLeft: `2px solid ${color}`, paddingLeft: 8 }}>{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Empty() { return <div className="text-sm mt-4" style={{ color: "var(--taupe)" }}>No visitor questions in this window yet.</div>; }

function LoginGate({ onSuccess }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr(""); setBusy(true);
    try { const r = await staffLogin(phone.replace(/\D/g, "").slice(-10), password); localStorage.setItem(ADMIN_TOKEN_KEY, r.token); onSuccess(); }
    catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen">
      <div className="max-w-[440px] mx-auto px-4 sm:px-8 py-16">
        <Eyebrow>Chat Insights · Admin</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl">Staff sign-in</h1>
        <Panel className="p-6 mt-6">
          <MonoLabel>Admin / DevAdmin phone</MonoLabel>
          <input data-testid="insights-login-phone" className="input-x mt-3" placeholder="10-digit phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <MonoLabel className="mt-4 block">Password</MonoLabel>
          <input data-testid="insights-login-password" type="password" className="input-x mt-3" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          {err && <div data-testid="insights-login-err" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
          <button data-testid="insights-login-btn" onClick={submit} disabled={busy} className="btn-red mt-5 w-full"><LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}</button>
        </Panel>
      </div>
    </div>
  );
}
